import { createStore } from '@oneappexchange/lwc-wired-store';
import { setAutoFreeze } from 'immer';
export function createI18nStore({
  bundledI18n,
  i18nModuleLoaders,
  defaultLanguage = 'en',
  missingTextSymbol = '[ERROR_I18N]',
  storeName = 'I18N',
  developmentMode = false
}) {
  // Immer auto-freezing is causing issues with i18n proxies, disable it: // https://immerjs.github.io/immer/freezing
  setAutoFreeze(false);
  function handleMessageFunctionException(key, message) {
    const errorMessage = `Encountered i18n exception for entry '${key}', this can happen if you don't pass the required parameters to the message function, or accessed a message function that requires parameters from the i18nStaticCache: ${message}`;
    if (developmentMode) {
      throw new Error(errorMessage);
    }
    console.error(errorMessage);
  }
  async function resolveBundledI18n() {
    return {
      default: bundledI18n
    };
  }
  /**
   * Converts an i18n module into a nested proxy that tracks access, and creates a cache to speed up field access.
   *
   * This allows us to customize the query behavior, for example:
   *  - Return entries from the default language if they are missing in the selected language.
   *  - Immediately call a messageformat function and cache its results upon access so we can treat its fields like static strings.
   */
  function createI18nProxy(i18nModule, i18nModuleCache, i18nCache, selector) {
    function createProxy(object, cache, defaultTarget, defaultCache) {
      return new Proxy(object, {
        get: function getter(target, property) {
          // Avoid serializing proxy to JSON when Redux Dev tools is enabled.
          // This prevents exceptions related to calling message functions that use parameters.
          if (developmentMode && property === 'toJSON' && (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          window.__REDUX_DEVTOOLS_EXTENSION__ || window.top.__REDUX_DEVTOOLS_EXTENSION__)) {
            return () => null;
          }
          // Ignore symbol accesses (likely some native JS operation)
          if (typeof property === 'symbol') {
            return Reflect.get(target, property);
          }
          if (cache[property]) {
            return cache[property];
          }
          const type = typeof target[property];
          if (type === 'function') {
            try {
              const i18nValue = selector ? selector(target[property]) : target[property];
              cache[property] = i18nValue;
              return i18nValue;
            } catch (exception) {
              handleMessageFunctionException(property.toString(), exception.message);
            }
            return missingTextSymbol;
          } else if (type === 'object') {
            const i18nProxy = createProxy(target[property], {}, defaultTarget[property], defaultCache[property] || {});
            cache[property] = i18nProxy;
            return i18nProxy;
          } else if (type === 'undefined') {
            console.warn(`Failed to find i18n entry with key '${property.toString()}' in the currently selected language bundle, looking in default language bundle`);
            if (defaultCache[property]) {
              return defaultCache[property];
            }
            const defaultType = typeof defaultTarget[property];
            if (defaultType === 'function') {
              try {
                const i18nValue = selector ? selector(defaultTarget[property]) : defaultTarget[property];
                defaultCache[property] = i18nValue;
                return i18nValue;
              } catch (exception) {
                handleMessageFunctionException(property.toString(), exception.message);
              }
              return missingTextSymbol;
            } else if (defaultType === 'object') {
              const i18nProxy = createProxy(defaultTarget[property], {}, defaultTarget[property], {});
              defaultCache[property] = i18nProxy;
              return i18nProxy;
            }
          }
          let errorMessage;
          if (type === 'undefined') {
            errorMessage = `Failed to find i18n entry '${property.toString()}' in both the selected and default i18n text bundles, please check your spelling and verify the key exists in the default i18n file.`;
          } else {
            errorMessage = `Invalid i18n type '${type}' for entry '${property.toString()}, this should not happen, please file an issue on the lwc-wired repository: https://github.com/OneAppExchange/lwc-wired/`;
          }
          if (developmentMode) {
            console.error(errorMessage);
          } else {
            console.warn(errorMessage);
          }
          return missingTextSymbol;
        }
      });
    }
    return createProxy(i18nModule, i18nModuleCache, bundledI18n, i18nCache);
  }
  const accessCache = {
    [defaultLanguage]: {}
  };
  const staticCache = {
    [defaultLanguage]: {}
  };
  /**
   * Converts an i18n module into a nested proxy that tracks access, and creates a cache of messageformat functions.
   * This allows us to fallback to bundled language if the selected language is missing an entry.
   */
  function createI18nAccessProxy(i18nModule, i18nModuleCache) {
    return createI18nProxy(i18nModule, i18nModuleCache, accessCache[defaultLanguage]);
  }
  /**
   * Converts an i18n module into a nested proxy that tracks access, and creates a cache of messageformat function results (localized messages with no parameters).
   * This allows us to query strings from i18n in LWC templates, just declare your i18n prop in your LWC element like so: @wire(WireI18n) i18n
   */
  function createI18nStaticProxy(i18nModule, i18nModuleCache) {
    return createI18nProxy(i18nModule, i18nModuleCache, staticCache[defaultLanguage], messageFunction => messageFunction());
  }
  const initialState = {
    isLoading: false,
    currentLanguage: defaultLanguage,
    i18n: createI18nAccessProxy(bundledI18n, accessCache[defaultLanguage]),
    i18nStaticCache: createI18nStaticProxy(bundledI18n, staticCache[defaultLanguage])
  };
  function createI18nActions(set) {
    return {
      async setLanguage(languageCode = defaultLanguage) {
        if (!accessCache[languageCode]) {
          accessCache[languageCode] = {};
        }
        if (!staticCache[languageCode]) {
          staticCache[languageCode] = {};
        }
        let resolveModule = i18nModuleLoaders[languageCode];
        if (!resolveModule) {
          if (developmentMode) {
            throw new Error(`Missing i18n module for language: ${languageCode}`);
          }
          resolveModule = resolveBundledI18n;
        }
        set(function setIsLoading(draft) {
          draft.isLoading = true;
        });
        const i18nModule = (await resolveModule()).default;
        set(function setLanguage(draft) {
          draft.isLoading = false;
          draft.currentLanguage = languageCode;
          draft.i18n = createI18nAccessProxy(i18nModule, accessCache[languageCode]);
          draft.i18nStaticCache = createI18nStaticProxy(i18nModule, staticCache[languageCode]);
        });
      }
    };
  }
  const i18nStore = createStore(initialState, createI18nActions, storeName, developmentMode);
  return i18nStore;
}